<template>
  <admin-layout>
    <div slot="content" class="admin-dashboard">
      <v-layout align-center justify-space-between class="mb-4" wrap>
        <h1 class="dashboard-title">Dashboard Licenciamento</h1>
        <router-link :to="{ name: 'ApplicationList' }" class="applications-link">
          Ver Requerimentos
          <v-icon size="18" color="#1976d2">mdi-arrow-right</v-icon>
        </router-link>
      </v-layout>

      <div v-if="loading" class="text-xs-center py-5">
        <v-progress-circular indeterminate color="green" :size="60" :width="5"/>
      </div>

      <div v-else-if="error" class="text-xs-center py-5">
        <p class="error-message">Não foi possível carregar os dados do dashboard.</p>
        <v-btn color="green" dark @click="fetchMetrics">Tentar novamente</v-btn>
      </div>

      <div v-else-if="metrics">
        <!-- Cards de destaque e quantitativos -->
        <div class="top-cards mb-4">
          <v-card flat class="highlight-card">
            <div class="highlight-amount">{{ metrics.applications_last_30_days }}</div>
            <div class="highlight-label">Requerimentos nos últimos 30 dias</div>
          </v-card>

          <v-card v-for="card in quantitativeCards" :key="card.key" flat class="highlight-card">
            <div class="highlight-amount">{{ card.amount }}</div>
            <div class="highlight-label">{{ card.label }}</div>
          </v-card>
        </div>

        <!-- Gráfico de barras por status -->
        <v-card flat class="chart-card mb-4">
          <v-layout align-center justify-space-between wrap>
            <div class="section-title">Requerimentos por status</div>
            <v-btn-toggle v-model="period" mandatory class="mb-3" @change="fetchMetrics">
              <v-btn flat :value="'30d'">Últimos 30 Dias</v-btn>
              <v-btn flat :value="'6m'">Últimos 6 Meses</v-btn>
              <v-btn flat :value="'all'">Geral</v-btn>
            </v-btn-toggle>
          </v-layout>
          <apexchart
            type="bar"
            height="320"
            :options="chartOptions"
            :series="chartSeries"
          />
        </v-card>

        <!-- Tabelas -->
        <div class="tables-grid">
          <v-card flat class="ranking-card">
            <div class="section-title">Processos analisados por analista (últimos 30 dias)</div>
            <v-data-table
              :headers="rankingHeaders"
              :items="rankingItems"
              hide-actions
              no-data-text="Nenhuma análise no período."
              class="ranking-table"
            >
              <template slot="items" slot-scope="props">
                <td>{{ props.item.name }}</td>
                <td class="text-xs-right">{{ props.item.count }}</td>
              </template>
            </v-data-table>
          </v-card>

          <v-card flat class="ranking-card">
            <div class="section-title">Processos por requerente (últimos 30 dias) &mdash; Top 10</div>
            <v-data-table
              :headers="applicantHeaders"
              :items="applicantItems"
              hide-actions
              no-data-text="Nenhum processo no período."
              class="ranking-table"
            >
              <template slot="items" slot-scope="props">
                <td>
                  <div>{{ props.item.name }}</div>
                  <div class="applicant-document">{{ props.item.cpf_cnpj }}</div>
                </td>
                <td class="text-xs-right">{{ props.item.count }}</td>
              </template>
            </v-data-table>
          </v-card>
        </div>
      </div>
    </div>
  </admin-layout>
</template>

<script>
import AdminLayout from '@/layouts/AdminLayout';
import DashboardService from '@/services/dashboard/DashboardService';

const STATUS_META = [
  { key: 'waiting_to_be_sent', title: 'A ENVIAR', color: '#616161' },
  { key: 'pending', title: 'PENDENTE', color: '#e53935' },
  { key: 'waiting_for_review', title: 'A ANALISAR', color: '#f9a825' },
  { key: 'in_review', title: 'EM ANÁLISE', color: '#fb8c00' },
  { key: 'approved', title: 'APROVADO', color: '#43a047' },
  { key: 'intimated', title: 'INTIMAÇÃO', color: '#f44336' },
];

const TOTAL_META = { title: 'TOTAL', color: '#1976d2' };

export default {
  name: 'AdminDashboard',
  components: {
    AdminLayout,
  },
  data() {
    return {
      period: '30d',
      loading: false,
      error: false,
      metrics: null,
      rankingHeaders: [
        { text: 'Analista', value: 'name', sortable: false, align: 'left' },
        { text: 'Quantidade', value: 'count', sortable: false, align: 'right' },
      ],
      applicantHeaders: [
        { text: 'Requerente', value: 'name', sortable: false, align: 'left' },
        { text: 'Processos', value: 'count', sortable: false, align: 'right' },
      ],
    };
  },
  computed: {
    // Total é a soma das colunas exibidas, para que a barra sempre feche com o
    // que está no gráfico mesmo que a API traga status fora de STATUS_META.
    statusValues() {
      if (!this.metrics) return [];
      const counts = this.metrics.status_counts || {};
      return STATUS_META.map((s) => counts[s.key] || 0);
    },
    chartSeries() {
      if (!this.metrics) return [];
      const total = this.statusValues.reduce((sum, value) => sum + value, 0);
      return [
        {
          name: 'Requerimentos',
          data: [...this.statusValues, total],
        },
      ];
    },
    chartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            distributed: true,
            columnWidth: '55%',
            dataLabels: { position: 'top' },
          },
        },
        colors: [...STATUS_META.map((s) => s.color), TOTAL_META.color],
        dataLabels: {
          enabled: true,
          offsetY: -22,
          style: {
            fontSize: '13px',
            fontWeight: 600,
            colors: ['#333'],
          },
          background: { enabled: false },
        },
        legend: { show: false },
        xaxis: {
          categories: [...STATUS_META.map((s) => s.title), TOTAL_META.title],
        },
        yaxis: {
          labels: {
            formatter: (val) => Math.round(val),
          },
        },
        tooltip: {
          y: { formatter: (val) => `${val}` },
        },
      };
    },
    quantitativeCards() {
      if (!this.metrics) return [];
      const infractions = this.metrics.infractions || {};
      return [
        { key: 'defesas', label: 'Defesas abertas (30 dias)', amount: this.metrics.defesas_last_30_days || 0 },
        { key: 'infractions_month', label: 'Autos de Infração (mês)', amount: infractions.current_month || 0 },
        { key: 'infractions_90', label: 'Autos de Infração (90 dias)', amount: infractions.last_90_days || 0 },
      ];
    },
    rankingItems() {
      if (!this.metrics || !this.metrics.analyses_by_admin_last_30_days) return [];
      return [...this.metrics.analyses_by_admin_last_30_days]
        .sort((a, b) => (b.count || 0) - (a.count || 0));
    },
    applicantItems() {
      if (!this.metrics || !this.metrics.applications_by_person_last_30_days) return [];
      return this.metrics.applications_by_person_last_30_days;
    },
  },
  mounted() {
    this.fetchMetrics();
  },
  methods: {
    fetchMetrics() {
      this.loading = true;
      this.error = false;
      DashboardService.metrics(this.period)
        .then((response) => {
          this.metrics = response.data;
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.dashboard-title {
  font-size: 28px;
  font-weight: 700;
  color: #333;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin-bottom: 12px;
}
.top-cards {
  display: flex;
  align-items: stretch;
  gap: 16px;
}
.highlight-card {
  flex: 1 1 0;
  min-width: 0;
  border: 1px solid #ddd;
  border-radius: 6px;
  border-left: 5px solid #1976d2;
  padding: 16px 24px;
  text-align: left;
}
.applications-link {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #1976d2;
  text-decoration: none;
  white-space: nowrap;

  .v-icon {
    margin-left: 4px;
  }

  &:hover {
    text-decoration: underline;
  }
}
.highlight-amount {
  font-size: 34px;
  font-weight: 700;
  line-height: 1.1;
  color: #1976d2;
}
.highlight-label {
  font-size: 14px;
  color: #666;
}
.chart-card,
.ranking-card {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
}
.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  grid-gap: 16px;
}
.applicant-document {
  font-size: 12px;
  color: #888;
}
.error-message {
  font-size: 15px;
  color: #666;
  margin-bottom: 16px;
}
::v-deep .ranking-table .v-datatable tbody td,
::v-deep .ranking-table .v-datatable thead th {
  font-size: 13px;
}
</style>
